import React, { useState } from "react";
import { X, Image } from "lucide-react";
import { postFrontAddEdit } from "../../../services/postService";
import { toast } from "react-toastify";

const PostCreateModal = ({ onClose }) => {
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detectedSlug, setDetectedSlug] = useState(null);

  // 🔥 Detect job slug from URL
  const extractJobSlug = (text) => {
    const jobRegex =
      /(?:https?|wss?):\/\/(?:www\.)?spryhub\.com\/jobs\/([a-zA-Z0-9-_]+)/;

    const match = text.match(jobRegex);
    return match ? match[1] : null;
  };

  const handleContentChange = (e) => {
    const value = e.target.value;
    setContent(value);

    const slug = extractJobSlug(value);
    setDetectedSlug(slug);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages((prev) => [...prev, ...files]);
  };

  const handleSubmit = async () => {
    if (!content && images.length === 0) {
      toast.error("Post content required");
      return;
    }

    const formData = new FormData();
    formData.append("content[en][title]", content);

    if (detectedSlug) {
      formData.append("type", "post");
      formData.append("postRefId", detectedSlug);
    } else {
      formData.append("type", "post");
    }

    images.forEach((file, index) => {
      formData.append(`mediaGallery[${index}][media]`, file);
      formData.append(`mediaGallery[${index}][ordering]`, index);
    });

    try {
      setLoading(true);

      const res = await postFrontAddEdit(formData); // ✅ store response

      if (res?.status === 201) {
        toast.success("Post created successfully");

        onClose(res?.data);
      } else {
        toast.error("Failed to create post");
      }
    } catch (err) {
      toast.error("Failed to create post");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-lg">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="font-semibold text-lg">Create Post</h3>
          <button onClick={() => onClose(null)}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <textarea
            value={content}
            onChange={handleContentChange}
            placeholder="What’s on your mind?"
            className="w-full min-h-[120px] outline-none resize-none text-sm"
          />

          {/* 🔗 Job Detection Preview */}
          {detectedSlug && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-lg p-3">
              🔗 Job link detected — This will be shared as a referral post
            </div>
          )}

          {/* Image Preview */}
          {images.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {images.map((file, i) => (
                <img
                  key={i}
                  src={URL.createObjectURL(file)}
                  alt=""
                  className="rounded-lg object-cover h-28 w-full"
                />
              ))}
            </div>
          )}

          {/* Upload */}
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
            <Image size={18} />
            Add Photo
            <input
              type="file"
              multiple
              hidden
              accept="image/*"
              onChange={handleImageUpload}
            />
          </label>
        </div>

        {/* Footer */}
        <div className="p-4 border-t">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-palatte text-white py-2 rounded-full font-semibold disabled:opacity-50"
          >
            {loading ? "Posting..." : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostCreateModal;
